/**
 * Garment Orders — the commercial intake list (ADR-0039), first item in the
 * Garment section. An order is logged the moment a buyer commits, and it may
 * cover several GARMENTS on one PO (ADR-0043) — engineering links a Style
 * File per garment from the order detail page, so this list surfaces "N of M
 * linked" rather than a single quick-link action. KPI strip (active orders ·
 * pieces · awaiting style file · delivery this week) + status tabs + search +
 * the shared DataTableCard / cursor-stack list grammar (mirrors
 * styles-list-page.tsx).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { CalendarClock, ClipboardList, Link2, Package, Plus, Search, Shirt } from "lucide-react";
import {
  Alert,
  Button,
  TabsList,
  TabsRoot,
  TabsTrigger,
  type StatusTone,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { formatDate } from "../../../lib/format.js";
import { orderStatusLabel, orderStatusTone, type GarmentOrderStatus } from "../lib/order-status.js";
import { dateRelative } from "../lib/relative-date.js";
import { avatarClassFor, initialsFor } from "../../master-list/lib/avatar-palette.ts";
import { DataTableCard, ListPagination } from "../../master-list/index.ts";

type Tab = "all" | "open" | "accepted" | "linked" | "closed";

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "all", label: "All" },
  { id: "open", label: "Open" },
  { id: "accepted", label: "Accepted" },
  { id: "linked", label: "Style linked" },
  { id: "closed", label: "Closed" },
];

interface OrderRow {
  id: string;
  orderNo: string;
  description: string | null;
  status: GarmentOrderStatus;
  buyerName: string;
  buyerRef: string | null;
  deliveryDate: string | null;
  totalQty: number;
  sizeCount: number;
  colourCount: number;
  itemCount: number;
  firstItemName: string | null;
  linkedItemCount: number;
}

export function GarmentOrdersListPage() {
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const serverStatus = tab === "all" ? undefined : (tab as GarmentOrderStatus);

  // Cursor-stack pagination (the shared list pattern — see styles list).
  const PAGE_SIZE = 50;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];
  useEffect(() => {
    setCursorStack([]);
  }, [tab]);

  const list = trpc.garmentOrders.list.useQuery(
    {
      limit: PAGE_SIZE,
      ...(serverStatus ? { status: serverStatus } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  const rows = useMemo(() => list.data?.items ?? [], [list.data?.items]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.orderNo.toLowerCase().includes(q) ||
        (r.description ?? "").toLowerCase().includes(q) ||
        (r.firstItemName ?? "").toLowerCase().includes(q) ||
        r.buyerName.toLowerCase().includes(q) ||
        (r.buyerRef ?? "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const counts = list.data?.counts;
  const kpis = list.data?.kpis;

  const pageNumber = cursorStack.length + 1;
  const showingFrom = rows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + rows.length;
  const totalRows = counts ? (serverStatus ? counts[serverStatus] : counts.all) : undefined;

  const tabCount = (id: Tab): number | undefined =>
    counts ? (id === "all" ? counts.all : counts[id]) : undefined;

  const visiblePieces = filtered.reduce((sum, r) => sum + r.totalQty, 0);

  const renderRow = (row: OrderRow, serial: number) => {
    const rel = dateRelative(row.deliveryDate);
    const subtitle = row.description ?? row.firstItemName ?? "";
    return (
      <tr key={row.id} className="border-t border-line transition-colors hover:bg-raised">
        <td className="px-4 py-3 text-right font-mono text-xs text-muted">{serial}</td>
        <td className="px-4 py-3">
          <div className="min-w-0">
            <Link
              to="/merchandising/orders/$orderId"
              params={{ orderId: row.id }}
              className="block truncate font-mono text-sm font-semibold text-ink hover:text-sky-700"
            >
              {row.orderNo}
            </Link>
            {subtitle ? (
              <span className="block truncate text-xs text-muted">{subtitle}</span>
            ) : null}
          </div>
        </td>
        <td className="px-3 py-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              aria-hidden
              className={`grid h-8 w-8 shrink-0 place-items-center rounded-md text-xs font-bold ${avatarClassFor(row.buyerName)}`}
            >
              {initialsFor(row.buyerName)}
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-ink-2">{row.buyerName}</div>
              <div className="truncate text-xs text-muted">
                {row.buyerRef ? `Ref ${row.buyerRef}` : "No buyer ref"}
              </div>
            </div>
          </div>
        </td>
        <td className="px-3 py-3">
          <span className="inline-flex items-center gap-1 rounded-full border border-line bg-bg px-2 py-0.5 text-xs text-muted">
            <Shirt size={11} aria-hidden />
            {row.itemCount} {row.itemCount === 1 ? "garment" : "garments"}
          </span>
        </td>
        <td className="px-3 py-3">
          <div className="font-mono text-sm text-ink-2">
            {row.deliveryDate ? formatDate(row.deliveryDate) : "—"}
          </div>
          {rel ? <div className={`text-xs ${rel.className}`}>{rel.label}</div> : null}
        </td>
        <td className="px-3 py-3 text-right">
          <div className="font-mono text-sm font-semibold text-ink">
            {row.totalQty.toLocaleString("en-IN")}
          </div>
          <div className="text-xs text-muted">
            {row.colourCount} {row.colourCount === 1 ? "colour" : "colours"} · {row.sizeCount}{" "}
            {row.sizeCount === 1 ? "size" : "sizes"}
          </div>
        </td>
        <td className="px-3 py-3">
          <Link
            to="/merchandising/orders/$orderId"
            params={{ orderId: row.id }}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-2 hover:text-sky-700"
          >
            <Link2 size={13} aria-hidden />
            {row.linkedItemCount} / {row.itemCount} linked
          </Link>
        </td>
        <td className="px-3 py-3">
          <OrderStatusBadge status={row.status} />
        </td>
      </tr>
    );
  };

  const tableEl = (
    <table className="w-full min-w-[880px] table-fixed text-sm">
      <colgroup>
        <col className="w-12" />
        <col />
        <col className="w-56" />
        <col className="w-32" />
        <col className="w-36" />
        <col className="w-28" />
        <col className="w-36" />
        <col className="w-36" />
      </colgroup>
      <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
        <tr>
          <th className="px-4 py-3 text-right font-medium">#</th>
          <th className="px-4 py-3 text-left font-medium">Order</th>
          <th className="px-3 py-3 text-left font-medium">Buyer</th>
          <th className="px-3 py-3 text-left font-medium">Garments</th>
          <th className="px-3 py-3 text-left font-medium">Delivery</th>
          <th className="px-3 py-3 text-right font-medium">Pieces</th>
          <th className="px-3 py-3 text-left font-medium">Style files</th>
          <th className="px-3 py-3 text-left font-medium">Status</th>
        </tr>
      </thead>
      <tbody>
        {list.isLoading ? (
          <tr>
            <td colSpan={8} className="px-3 py-8 text-center text-sm text-muted">
              Loading orders…
            </td>
          </tr>
        ) : filtered.length === 0 ? (
          <tr>
            <td colSpan={8} className="px-3 py-10 text-center text-sm text-muted">
              {rows.length === 0 ? (
                <>
                  No orders yet.{" "}
                  <Link to="/merchandising/orders/new" className="text-sky-700 hover:underline">
                    Log your first buyer order.
                  </Link>
                </>
              ) : (
                "No orders match the current filters."
              )}
            </td>
          </tr>
        ) : (
          filtered.map((row, i) => renderRow(row, showingFrom + i))
        )}
      </tbody>
    </table>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-ink">Orders</h1>
          <p className="mt-1 text-sm text-muted">
            Log a buyer&apos;s commitment the moment it lands — link the Style File when
            engineering picks it up.
          </p>
        </div>
        <Button asChild>
          <Link to="/merchandising/orders/new">
            <Plus size={15} />
            New order
          </Link>
        </Button>
      </div>

      {list.error ? (
        <Alert intent="bad" title="Couldn't load orders" description={list.error.message} />
      ) : null}

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <OrderKpi
          tone="sky"
          icon={<ClipboardList size={16} />}
          label="Active orders"
          value={String(kpis?.activeCount ?? 0)}
          meta="open + style linked"
        />
        <OrderKpi
          tone="ok"
          icon={<Package size={16} />}
          label="Pieces on order"
          value={(kpis?.activePieces ?? 0).toLocaleString("en-IN")}
          meta="across active orders"
        />
        <OrderKpi
          tone="warn"
          icon={<Link2 size={16} />}
          label="Awaiting style file"
          value={String(kpis?.unlinkedCount ?? 0)}
          meta="engineering queue"
        />
        <OrderKpi
          tone="muted"
          icon={<CalendarClock size={16} />}
          label="Delivery this week"
          value={String(kpis?.deliveryThisWeekCount ?? 0)}
          meta="next 7 days"
        />
      </section>

      <TabsRoot value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList aria-label="Order status">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} count={tabCount(t.id)}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </TabsRoot>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order no, item, buyer, or buyer ref…"
            className="w-full rounded-md border border-line bg-paper py-1.5 pl-8 pr-3 text-sm text-ink placeholder:text-muted focus:border-sky-400 focus:outline-none"
          />
        </div>
      </div>

      <DataTableCard
        footer={
          filtered.length > 0
            ? [
                { label: "Visible", value: filtered.length },
                { label: "Total pieces", value: visiblePieces.toLocaleString("en-IN") },
              ]
            : []
        }
        pagination={
          filtered.length > 0 ? (
            <ListPagination
              pageNumber={pageNumber}
              showingFrom={showingFrom}
              showingTo={showingTo}
              {...(typeof totalRows === "number" ? { total: totalRows } : {})}
              sortLabel="sorted by newest first"
              hasPrev={cursorStack.length > 0}
              hasNext={!!list.data?.nextCursor}
              onPrev={() => setCursorStack((s) => s.slice(0, -1))}
              onNext={() => {
                const next = list.data?.nextCursor;
                if (next) setCursorStack((s) => [...s, next]);
              }}
            />
          ) : undefined
        }
      >
        {tableEl}
      </DataTableCard>

    </div>
  );
}

const BADGE_TONE: Record<StatusTone, string> = {
  sky: "border-sky-200 bg-sky-50 text-sky-700",
  ok: "border-emerald-200 bg-emerald-50 text-emerald-700",
  warn: "border-amber-200 bg-amber-50 text-amber-700",
  bad: "border-rose-200 bg-rose-50 text-rose-700",
  muted: "border-line bg-raised text-muted",
};

function OrderStatusBadge({ status }: { status: GarmentOrderStatus }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${BADGE_TONE[orderStatusTone(status)]}`}
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
      {orderStatusLabel(status)}
    </span>
  );
}

function OrderKpi({
  tone,
  icon,
  label,
  value,
  meta,
}: {
  tone: "sky" | "ok" | "warn" | "muted";
  icon: React.ReactNode;
  label: string;
  value: string;
  meta: string;
}) {
  const toneClass: Record<string, string> = {
    sky: "bg-sky-100 text-sky-700",
    ok: "bg-ok-100 text-ok",
    warn: "bg-warn-100 text-warn",
    muted: "bg-line text-ink-2",
  };
  return (
    <div className="flex items-center gap-3 rounded-md border border-line bg-paper p-3.5 shadow-sm">
      <span
        aria-hidden
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-md ${toneClass[tone]}`}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <div className="truncate text-xs text-muted">{label}</div>
        <div className="font-mono text-lg font-bold leading-tight text-ink">{value}</div>
        <div className="truncate text-xs text-muted">{meta}</div>
      </div>
    </div>
  );
}
